import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordFields } from "@/components/PasswordFields";
import { KeyRound, Mail, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState("email");
  const [step, setStep] = useState("request"); // request | sms | email-sent
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [pwValid, setPwValid] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email, method, origin_url: window.location.origin });
      if (method === "sms") {
        if (data.no_phone) { toast.error("No verified phone on this account. Try the email option."); return; }
        if (data.dev_code) toast.info(`Dev mode: your reset code is ${data.dev_code}`);
        else toast.success("A reset code was sent via SMS.");
        setStep("sms");
      } else {
        if (data.dev_link) toast.info(`Dev mode: reset link → ${data.dev_link}`);
        setStep("email-sent");
      }
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const resetViaSms = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: newPassword });
      toast.success("Password reset. Please log in.");
      navigate("/login");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (step === "email-sent") {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-20 text-center">
        <Mail className="w-14 h-14 text-primary mx-auto" strokeWidth={1.5} />
        <h1 className="font-display text-3xl font-semibold mt-4">Check your email</h1>
        <p className="text-muted-foreground mt-2">If an account exists for <b>{email}</b>, we've sent a link to reset your password. It expires in 1 hour.</p>
        <Link to="/login"><Button variant="outline" className="rounded-sm mt-8" data-testid="back-to-login">Back to login</Button></Link>
      </div>
    );
  }

  if (step === "sms") {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-20">
        <div className="flex items-center gap-2 text-primary"><Smartphone className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">SMS reset</span></div>
        <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">Enter code & new password</h1>
        <form onSubmit={resetViaSms} className="mt-8 space-y-5">
          <div>
            <Label htmlFor="code">6-digit code</Label>
            <Input id="code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} inputMode="numeric" required
              placeholder="123456" className="mt-1.5 rounded-sm tracking-[0.4em] text-center" data-testid="reset-sms-code" />
          </div>
          <div>
            <PasswordFields onChange={(pw, v) => { setNewPassword(pw); setPwValid(v); }} />
          </div>
          {error && <p className="text-destructive text-sm" data-testid="reset-error">{error}</p>}
          <Button type="submit" disabled={loading || !pwValid || code.length < 6} className="w-full rounded-sm" data-testid="reset-sms-submit">{loading ? "Resetting…" : "Reset password"}</Button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-20">
      <div className="flex items-center gap-2 text-primary"><KeyRound className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">Recover</span></div>
      <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">Forgot your password?</h1>
      <p className="text-muted-foreground mt-2">Choose how you'd like to reset it.</p>
      <form onSubmit={request} className="mt-8 space-y-5">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-sm" data-testid="forgot-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[{ k: "email", l: "Email link", i: Mail }, { k: "sms", l: "SMS code", i: Smartphone }].map((m) => (
            <button type="button" key={m.k} onClick={() => setMethod(m.k)} data-testid={`method-${m.k}`}
              className={`flex items-center gap-2 border rounded-sm p-3 transition-all ${method === m.k ? "border-primary ring-2 ring-primary bg-primary/5" : "border-border hover:border-secondary"}`}>
              <m.i className="w-5 h-5" /><span className="text-sm font-medium">{m.l}</span>
            </button>
          ))}
        </div>
        {error && <p className="text-destructive text-sm" data-testid="forgot-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full rounded-sm" data-testid="forgot-submit">{loading ? "Sending…" : "Send reset instructions"}</Button>
        <Link to="/login" className="block text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back">← Back to login</Link>
      </form>
    </div>
  );
}
